import { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { generateResult, getReading, getSharedReading } from '../lib/api';
import type { Reading, DrawnCard, Topic, SpreadType, TarotCard, ReadingResult, CardReading, InsightSection } from '../lib/types';
import { getCardFrontImageSrc } from '../lib/cardImages';
import { useReadingFlowBackGuard } from '../lib/navigationGuard';
import { generateDemoCardSelections, readDemoCardSelections, writeDemoCardSelections } from '../lib/demoCards';
import { trackEvent } from '../lib/analytics';
import UiIcon from '../components/common/UiIcon';

const POLL_INTERVAL = 3000;
const MAX_POLL_ATTEMPTS = 60;
const MODAL_CARD_ANIMATIONS = ['pop'] as const;
type ModalCardAnimation = typeof MODAL_CARD_ANIMATIONS[number];

interface CelticChapter {
  start: number;
  title: string;
  subtitle: string;
}

const CELTIC_CHAPTERS: CelticChapter[] = [
  { start: 0, title: '현재와 걸림돌', subtitle: '판의 표면과 마찰이 먼저 드러나는 구간' },
  { start: 2, title: '목표와 기반', subtitle: '의식하는 방향과 밑바닥 감정을 읽는 구간' },
  { start: 4, title: '흐름의 전개', subtitle: '최근 흔적과 가까운 미래를 보는 구간' },
  { start: 6, title: '나와 바깥 변수', subtitle: '태도, 환경, 두려움, 결말을 묶어 보는 구간' },
];

// Get position meanings based on spread type
const getPositionMeanings = (spread: SpreadType): string[] => {
  switch (spread) {
    case 'one-card':
      return ['현재 상황'];
    case 'three-card':
      return ['과거', '현재', '미래'];
    case 'celtic-cross':
      return [
        '현재 상황',
        '장애물',
        '목표/이상',
        '과거/기반',
        '최근 과거',
        '가까운 미래',
        '자신',
        '환경/타인',
        '희망/두려움',
        '최종 결과',
      ];
    default:
      return [];
  }
};

function inferSpreadFromCount(cardCount: number): SpreadType {
  if (cardCount === 1) return 'one-card';
  if (cardCount === 10) return 'celtic-cross';
  return 'three-card';
}

const GENERIC_POSITION_MEANING_PATTERN = /^(?:\d+번째\s*자리|[첫두세네다섯여섯일곱여덟아홉열]\s*자리)$/;
const DIGIT_BATCHIM_MAP: Record<string, boolean> = {
  '0': true,
  '1': true,
  '2': false,
  '3': true,
  '4': false,
  '5': false,
  '6': true,
  '7': true,
  '8': true,
  '9': false,
};
const NUMBER_BATCHIM_MAP: Record<string, boolean> = {
  ...DIGIT_BATCHIM_MAP,
  '10': true,
  '11': true,
  '12': false,
  '13': true,
  '14': false,
  '15': false,
  '16': true,
  '17': true,
  '18': true,
  '19': false,
  '20': false,
  '21': true,
};
const DIGIT_RIEUL_ENDING = new Set(['1', '7', '8']);

function getParticleTarget(text: string): string {
  return text
    .trim()
    .replace(/^[\s"'“”‘’([{<]+/u, '')
    .replace(/[\s"'“”‘’)\]}>.,!?…:;]+$/u, '');
}

function hasBatchim(text: string): boolean {
  const target = getParticleTarget(text);
  const numberMatch = target.match(/(\d+)$/);
  if (numberMatch) {
    return NUMBER_BATCHIM_MAP[numberMatch[1]] ?? DIGIT_BATCHIM_MAP[numberMatch[1].slice(-1)] ?? false;
  }

  const lastChar = Array.from(target).at(-1);
  if (!lastChar) return false;

  const code = lastChar.charCodeAt(0);
  if ((code >= 65 && code <= 90) || (code >= 97 && code <= 122)) {
    return false;
  }

  if (code >= 0xac00 && code <= 0xd7a3) {
    return ((code - 0xac00) % 28) !== 0;
  }

  return false;
}

function pickParticle(text: string, pair: '은는' | '이가' | '을를'): string {
  const withBatchim = hasBatchim(text);
  switch (pair) {
    case '은는':
      return withBatchim ? '은' : '는';
    case '이가':
      return withBatchim ? '이' : '가';
    case '을를':
      return withBatchim ? '을' : '를';
    default:
      return '';
  }
}

function pickDirectionalParticle(text: string): '로' | '으로' {
  const target = getParticleTarget(text);
  const numberMatch = target.match(/(\d+)$/);
  if (numberMatch) {
    const raw = numberMatch[1];
    const lastDigit = raw.slice(-1);
    const hasFinalConsonant = NUMBER_BATCHIM_MAP[raw] ?? DIGIT_BATCHIM_MAP[lastDigit] ?? false;
    return !hasFinalConsonant || DIGIT_RIEUL_ENDING.has(lastDigit) ? '로' : '으로';
  }

  const lastChar = Array.from(target).at(-1);
  if (!lastChar) return '로';

  const code = lastChar.charCodeAt(0);
  if (code >= 0xac00 && code <= 0xd7a3) {
    const finalConsonant = (code - 0xac00) % 28;
    return finalConsonant === 0 || finalConsonant === 8 ? '로' : '으로';
  }

  return '로';
}

function getDisplayPositionMeaning(spread: SpreadType, drawnCard: Partial<DrawnCard> | undefined, index: number): string {
  const raw = drawnCard?.positionMeaning?.trim();
  if (raw && !GENERIC_POSITION_MEANING_PATTERN.test(raw)) {
    return raw;
  }

  return getPositionMeanings(spread)[index] || `${index + 1}번째 카드`;
}

function getPrimaryDisplayKeyword(drawnCard: DrawnCard): string {
  const keywords = drawnCard.orientation === 'upright'
    ? drawnCard.card.keywords_upright
    : drawnCard.card.keywords_reversed;

  return keywords[0] || drawnCard.card.meaning_short;
}

function getSuitLabel(suit?: TarotCard['suit']): string {
  switch (suit) {
    case 'wands':
      return '완드';
    case 'cups':
      return '컵';
    case 'swords':
      return '소드';
    case 'pentacles':
      return '펜타클';
    default:
      return '-';
  }
}

function getCardDisplayNumber(card: TarotCard): string {
  if (typeof card.number === 'number') {
    return String(card.number);
  }

  const normalizedName = `${card.name_kr} ${card.name_en}`.toLowerCase();

  if (normalizedName.includes('시종') || normalizedName.includes('page')) return '시종';
  if (normalizedName.includes('기사') || normalizedName.includes('knight')) return '기사';
  if (normalizedName.includes('여왕') || normalizedName.includes('queen')) return '여왕';
  if (normalizedName.includes('왕') || normalizedName.includes('king')) return '왕';

  return '-';
}

function compactReadingText(value?: string): string | undefined {
  let normalized = value?.trim();

  if (!normalized) {
    return undefined;
  }

  normalized = normalized
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/\\\\n/g, '\n')
    .replace(/\\n/g, '\n');

  for (let index = 0; index < 2; index += 1) {
    if (!(normalized.startsWith('"') && normalized.endsWith('"'))) {
      break;
    }

    try {
      const parsed = JSON.parse(normalized);
      if (typeof parsed === 'string') {
        normalized = parsed.trim();
        continue;
      }
    } catch {
      break;
    }

    break;
  }

  normalized = normalized
    .replace(/\\"/g, '"')
    .replace(/\\\\/g, '\\')
    .replace(/\r\n/g, '\n')
    .trim();

  normalized = unwrapQuotedText(stripQuotedFragments(normalized))
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '"' && line !== "'" && line !== '“' && line !== '”')
    .join('\n')
    .trim();

  return normalized || undefined;
}

function splitDisplayParagraphs(value?: string): string[] {
  const normalized = compactReadingText(value);
  if (!normalized) {
    return [];
  }

  return normalized
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.replace(/\n+/g, ' ').trim())
    .filter(Boolean);
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function unwrapQuotedText(value: string): string {
  let current = value.trim();

  for (let index = 0; index < 3; index += 1) {
    if (
      (current.startsWith('"') && current.endsWith('"'))
      || (current.startsWith('“') && current.endsWith('”'))
      || (current.startsWith("'") && current.endsWith("'"))
    ) {
      current = current.slice(1, -1).trim();
      continue;
    }

    break;
  }

  return current;
}

function stripQuotedFragments(value: string): string {
  return value
    .replace(/"([가-힣A-Za-z0-9\s/·,&:-]+)"/g, '$1')
    .replace(/“([가-힣A-Za-z0-9\s/·,&:-]+)”/g, '$1');
}

function getCelticChapter(index: number, spread: SpreadType): CelticChapter | null {
  if (spread !== 'celtic-cross') return null;

  for (let chapterIndex = CELTIC_CHAPTERS.length - 1; chapterIndex >= 0; chapterIndex -= 1) {
    const chapter = CELTIC_CHAPTERS[chapterIndex];
    if (index >= chapter.start) {
      return chapter;
    }
  }

  return null;
}

function joinCardReadingBeats(cardReading?: Partial<CardReading> | null): string {
  if (!cardReading) return '';

  const parts = [
    compactReadingText(cardReading.hook),
    compactReadingText(cardReading.reading),
    compactReadingText(cardReading.connection),
    compactReadingText(cardReading.bridge),
  ].filter((part): part is string => Boolean(part));

  if (parts.length > 0) {
    return parts.join('\n\n');
  }

  return compactReadingText(cardReading.narration) || '';
}

function isLegacyCardReading(cardReading: CardReading): boolean {
  const text = joinCardReadingBeats(cardReading);
  return /첫 자리 자리|두 자리 자리|세 자리 자리|피하지 말고 정면으로 봐야|혼자 따로 읽기보다 앞뒤 흐름 속에서 봐야/.test(text);
}

function getStructuredCardReadings(result?: ReadingResult, fallbackCardReadings?: CardReading[]): CardReading[] {
  if (result?.cardReadings?.length) {
    const normalized = result.cardReadings
      .map((cardReading) => ({
        ...cardReading,
        narration: joinCardReadingBeats(cardReading) || cardReading.narration,
      }))
      .filter((cardReading) => Boolean(cardReading.narration));

    if (normalized.some((cardReading) => isLegacyCardReading(cardReading)) && fallbackCardReadings?.length) {
      return fallbackCardReadings;
    }

    return normalized;
  }

  const fromDetail = (result?.detail || '')
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter((paragraph) => paragraph.length > 0)
    .map((paragraph) => ({ narration: paragraph }));

  if (fromDetail.some((cardReading) => isLegacyCardReading(cardReading as CardReading)) && fallbackCardReadings?.length) {
    return fallbackCardReadings;
  }

  return fromDetail;
}

function normalizeInsightSection(section?: Partial<InsightSection> | null): InsightSection | null {
  const title = compactReadingText(section?.title);
  const body = compactReadingText(section?.body);

  if (!title || !body) {
    return null;
  }

  return {
    title,
    preview: compactReadingText(section?.preview),
    body,
  };
}

function polishInsightTitle(title: string, index: number): string {
  const normalized = title.trim();

  if (index === 0) {
    if (/처음 카드.*먼저 .*흔들림이야/.test(normalized)) {
      return '첫 장이 가리킨 건, 문제가 시작된 자리야';
    }
    if (/덮어도 남는 현재의 결/.test(normalized)) {
      return '첫 장에서 먼저 드러난 건, 숨길 수 없는 시작점이야';
    }
    if (/판이 시작되자마자 드러난 건/.test(normalized)) {
      return '첫 장을 보자마자, 어디서 꼬였는지가 먼저 보여';
    }
  }

  if (index === 1 && /겉문제보다 더 안쪽에서 걸리는 진짜 이유/.test(normalized)) {
    return '겉으로 보인 문제보다 더 안쪽의 이유가 있어';
  }

  if (index === 2 && /결말은 운보다 지금의 기준에서 갈라져/.test(normalized)) {
    return '마지막 장이 보여준 건, 지금 흐름의 방향이야';
  }

  return normalized;
}

function getInsightFocusLabel(sectionIndex: number, cards?: DrawnCard[]): string | null {
  if (!cards?.length) return null;

  if (sectionIndex === 0) {
    return cards.length === 1 ? '이 카드는' : '첫 장에 놓인 이 카드는';
  }

  if (sectionIndex === 1) {
    return cards.length === 1 ? '이 카드는' : cards.length === 3 ? '가운데 자리에 놓인 이 카드는' : '이번 중심 카드가';
  }

  if (sectionIndex === 2) {
    return cards.length === 1 ? '이 카드는' : '마지막 장에 놓인 이 카드는';
  }

  return null;
}

function polishInsightBody(body: string, sectionIndex: number, cards?: DrawnCard[]): string {
  const focusCard = sectionIndex === 0
    ? cards?.[0]
    : sectionIndex === 1
      ? cards?.[Math.min(1, (cards?.length || 1) - 1)]
      : sectionIndex === 2
        ? cards?.[cards.length - 1]
        : undefined;

  if (!focusCard) {
    return body;
  }

  const focusLabel = getInsightFocusLabel(sectionIndex, cards);
  if (!focusLabel) {
    return body;
  }

  return body.replace(
    new RegExp(`${escapeRegExp(focusCard.card.name_kr)}(?:은|는|이|가|을|를)`, 'u'),
    focusLabel,
  );
}

function finalizeInsightSection(section: InsightSection | null, index: number, cards?: DrawnCard[]): InsightSection | null {
  if (!section) return null;

  return {
    ...section,
    title: polishInsightTitle(section.title, index),
    body: polishInsightBody(section.body, index, cards),
  };
}

function getStructuredInsightSections(
  result?: ReadingResult,
  fallbackResult?: ReadingResult,
  cards?: DrawnCard[],
): InsightSection[] {
  const normalized = result?.insightSections
    ?.map((section, index) => finalizeInsightSection(normalizeInsightSection(section), index, cards))
    .filter((section): section is InsightSection => section !== null);

  if (normalized?.length) {
    return normalized;
  }

  const fallback = fallbackResult?.insightSections
    ?.map((section, index) => finalizeInsightSection(normalizeInsightSection(section), index, cards))
    .filter((section): section is InsightSection => section !== null);

  if (fallback?.length) {
    return fallback;
  }

  const synthesis = compactReadingText(result?.synthesis) || compactReadingText(result?.summary);
  const nextAction = compactReadingText(result?.nextAction) || compactReadingText(result?.advice);
  const caution = compactReadingText(result?.caution);
  const closing = compactReadingText(result?.closing);

  const sections: Array<InsightSection | null> = [
    synthesis ? {
      title: '카드들이 먼저 보여준 건, 이미 시작된 흐름이야',
      preview: '카드 여러 장이 같은 쪽을 가리키고 있어.',
      body: synthesis,
    } : null,
    nextAction ? {
      title: '지금 가장 먼저 정리해야 할 기준',
      preview: '방법보다 먼저 중심을 잡아야 해.',
      body: nextAction,
    } : null,
    caution ? {
      title: '좋아 보여도 그냥 넘기면 안 되는 지점',
      preview: '이 부분은 특히 조심해서 봐야 해.',
      body: caution,
    } : null,
    closing ? {
      title: '묘안이 마지막에 남기고 싶은 한마디',
      preview: '결국 남는 건 이 문장일 거야.',
      body: closing,
    } : null,
  ];

  return sections.filter((section): section is InsightSection => section !== null);
}

function buildDemoFallbackResult(spread: SpreadType, drawnCards: DrawnCard[]): ReadingResult {
  const firstKeyword = drawnCards[0] ? getPrimaryDisplayKeyword(drawnCards[0]) : '현재의 핵심';
  const middleKeyword = drawnCards[1] ? getPrimaryDisplayKeyword(drawnCards[1]) : firstKeyword;
  const lastKeyword = drawnCards[drawnCards.length - 1] ? getPrimaryDisplayKeyword(drawnCards[drawnCards.length - 1]) : firstKeyword;

  const cardReadings = drawnCards.map((drawnCard, index) => {
    const position = getDisplayPositionMeaning(spread, drawnCard, index);
    const cardName = `${drawnCard.card.name_kr}${pickParticle(drawnCard.card.name_kr, '은는')}`;
    const keyword = getPrimaryDisplayKeyword(drawnCard);
    const hook = spread === 'one-card'
      ? '한 장인데도 질문의 심장이 바로 보이네. 이번 카드는 돌려 말하지 않아.'
      : spread === 'three-card'
        ? (index === 0
          ? '첫 장부터 분위기가 선명하네. 이 문제는 어디서 어긋났는지부터 보여줘.'
          : index === 1
            ? '이제 가운데로 들어간다. 여기엔 네가 제일 신경 쓰는 본심이 걸려 있어.'
            : '마지막 장이 말을 아끼질 않네. 이 흐름이 결국 어디로 기우는지 여기서 드러나.')
        : (index === 0
          ? '판 한가운데부터 바로 드러나네. 지금 상황을 숨기지 않는 카드야.'
          : index === 1
            ? '이 장은 흐름을 막는 이유를 빠르게 꺼내. 어디가 제일 버거운지 선명해져.'
            : index === 2
              ? '겉으로 붙드는 목표가 여기서 보인다. 네가 의식적으로 붙잡는 방향이 뚜렷해.'
              : index === 3
                ? '겉보다 안쪽이 더 크게 움직이는 자리야. 밑바닥 감정을 그냥 지나치면 안 돼.'
                : index === 4
                  ? '이 카드는 바로 직전까지 남은 흔적을 보여줘. 왜 판이 여기까지 왔는지 힌트가 선명해.'
                  : index === 5
                    ? '이제 가까이 다가오는 전개가 보인다. 다음 장면의 공기가 여기서 먼저 스친다.'
                    : index === 6
                      ? '지금 네가 어떤 자세로 이 판을 버티는지 여기서 적나라하게 드러나.'
                      : index === 7
                        ? '이 자리는 네 바깥에서 작동하는 힘을 보여줘. 혼자만의 문제가 아니라는 뜻이야.'
                        : index === 8
                          ? '기대와 두려움이 같이 올라오는 자리야. 마음이 어디서 흔들리는지 더 숨길 수가 없어.'
                          : '마지막 장은 결말을 감추지 않네. 지금 판이 어디로 굳어지는지 여기서 보인다.');
    const reading = spread === 'one-card'
      ? `${cardName} 이번 질문에선 ${keyword} 쪽 대답을 바로 꺼내고 있어. 지금은 상황 설명보다, 이 카드가 왜 자꾸 마음에 걸리는지부터 보는 게 맞아.`
      : spread === 'three-card'
        ? (index === 0
          ? `${cardName} ${position}에 나온 걸 보면, 이야기의 시작엔 ${keyword} 쪽으로 먼저 마음이 쏠렸어. 너무 빨리 결론을 내렸거나, 불안을 덮으려고 먼저 움직인 선택이 이 장면과 닿아 있어.`
          : index === 1
            ? `${cardName} ${position}에 놓인 건, 지금 가장 크게 걸리는 게 결국 ${keyword} 쪽이라는 뜻이야. 겉으로 보이는 문제보다 흔들리면 안 된다는 압박이 더 크게 작동하는 자리로 읽혀.`
            : `${cardName} 마지막에 나온 건, 이 흐름이 ${keyword} 쪽으로 풀릴 여지가 있다는 뜻이야. 다만 저절로 좋아진다기보다, 지금부터 기준을 다시 세울수록 카드의 좋은 면이 살아나.`)
        : (index === 0
          ? `${cardName} ${position}에 놓인 걸 보면, ${keyword} 쪽 긴장이 이미 표면으로 올라와 있어. 지금은 사건 하나보다 판 전체가 어디서 눌리는지부터 읽는 게 맞아.`
          : index === 1
            ? `${cardName} ${position}에 놓인 건, 막히는 이유가 부족함보다 ${keyword} 뒤의 망설임에 더 가깝다는 뜻이야. 해결책보다 먼저 무엇이 흐름을 막는지 인정해야 해.`
            : index === 2
              ? `${cardName} ${position}에선, 네가 겉으로 붙드는 방향이 결국 ${keyword} 쪽이라는 게 보여. 지금은 원하는 미래보다 무엇을 이상처럼 붙들고 있는지 보는 게 더 중요해.`
              : index === 3
                ? `${cardName} ${position}에 놓인 걸 보면, 밑바닥엔 오래된 ${keyword} 감정이 깔려 있었어. 겉말보다 안쪽 습관을 읽어야 이 판이 풀린다.`
                : index === 4
                  ? `${cardName} ${position}에선, 최근까지 이어진 ${keyword}의 흔적이 아직 정리되지 않은 채 남아 있어. 왜 여기까지 왔는지 보려면 바로 직전 선택의 결을 놓치면 안 돼.`
                  : index === 5
                    ? `${cardName} ${position}에 놓인 건, ${keyword} 쪽 장면이 생각보다 빨리 가까워진다는 신호야. 미래를 예언처럼 보기보다 지금부터 무엇이 더 짙어질지 읽는 자리야.`
                    : index === 6
                      ? `${cardName} ${position}에선, 지금 네가 ${keyword} 쪽으로 버티는 습관이 먼저 보여. 상황보다 네 태도가 결과를 더 크게 흔들 수 있는 구간이야.`
                      : index === 7
                        ? `${cardName} ${position}에 놓인 걸 보면, 바깥에서도 ${keyword} 기운이 꽤 크게 작동하고 있어. 혼자만의 문제로 오해하면 판을 잘못 읽기 쉬워.`
                        : index === 8
                          ? `${cardName} ${position}에선, ${keyword}${pickParticle(keyword, '을를')} 바라는 마음과 두려워하는 마음이 같이 보여. 기대만큼 겁도 크다는 뜻이야.`
                          : `${cardName} ${position}에 놓인 건, 이 판이 결국 ${keyword} 쪽으로 굳어질 가능성이 높다는 뜻이야. 결말은 확정이 아니라 지금 패턴을 그대로 밀고 갔을 때의 가장 유력한 방향으로 읽어야 해.`);
    const connection = index === 0
      ? undefined
      : spread === 'three-card'
        ? (index === 1
          ? '앞 카드에서 드러난 조급함이 이번 카드에선 더 선명한 문제로 바뀌고 있어. 겉보다 속이 더 크게 긴장하고 있었다는 뜻이야.'
          : '이제 판의 무게중심이 앞 카드의 압박에서 마지막 카드가 말하는 방향으로 넘어가고 있어. 결말은 감정에 끌리느냐, 기준을 세우느냐에 달렸어.')
        : '앞 카드와 이어 보면, 이번 판의 초점이 표면의 장면에서 한 겹 더 안쪽 이유로 옮겨가고 있어.';
    const bridge = index < drawnCards.length - 1
        ? (spread === 'three-card'
          ? (index === 0
            ? '시작점은 봤어. 다음 장에서 지금 네가 제일 예민하게 반응하는 진짜 지점을 바로 볼게.'
            : '핵심은 거의 잡혔어. 마지막 장에서 이 판이 결국 어느 쪽으로 기우는지 확인해 보자.')
        : spread === 'celtic-cross'
          ? (() => {
            const nextPosition = getDisplayPositionMeaning(spread, drawnCards[index + 1], index + 1);
            return `좋아, 이제 ${nextPosition}${pickDirectionalParticle(nextPosition)} 넘어가 보자. 다음 카드는 방금 본 결을 더 분명한 이유로 끌어내릴 거야.`;
          })()
          : undefined)
      : undefined;

    return {
      hook,
      reading,
      connection,
      bridge,
      narration: joinCardReadingBeats({ hook, reading, connection, bridge }),
    };
  });

  const detail = cardReadings.map((cardReading, index) =>
    `[${drawnCards[index].card.name_kr}]\n${cardReading.narration}`
  ).join('\n\n');

  const synthesis = spread === 'one-card'
    ? `${drawnCards[0].card.name_kr}${pickParticle(drawnCards[0].card.name_kr, '이가')} 말하는 건 단순해. 답을 복잡하게 만드는 건 상황보다 네 안의 망설임이야.`
    : spread === 'three-card'
      ? `처음엔 ${firstKeyword} 쪽으로 마음이 먼저 쏠렸고, 가운데에서는 ${middleKeyword} 문제가 진짜 걸림돌로 드러났고, 마지막엔 ${lastKeyword} 쪽으로 방향이 열리고 있어. 지금 필요한 건 더 세게 밀어붙이는 게 아니라, 선택 기준을 다시 세우는 일이야.`
      : `켈틱 크로스 전체를 보면, 한 장의 좋고 나쁨보다 패턴이 더 중요하게 읽혀. 해결도 단순한 낙관보다 어디서 힘을 빼고 어디서 결정을 또렷하게 할지 가르는 데 달렸어.`;
  const nextAction = spread === 'one-card'
    ? '오늘 안에 가장 마음에 걸리는 감정 하나를 회피하지 말고 이름 붙여 봐.'
    : '지금 제일 무겁게 느껴지는 문제 하나를 골라, 계속 붙들 일과 이제 놓을 일을 나눠 적어 봐.';
  const caution = '겉으로 괜찮은 척 지나가도 몸과 마음은 이미 신호를 보내고 있어. 그걸 또 무시하면 같은 패턴이 반복돼.';
  const closing = spread === 'one-card'
    ? '답은 이미 꽤 가까이 와 있었어. 오늘 이 카드는 그걸 숨기지 말라고 정확히 짚어준 셈이야.'
    : spread === 'three-card'
      ? '세 장을 다 보고 나면 남는 건 하나야. 불안해서 먼저 움직이기보다, 기준부터 다시 세우는 것. 그게 이번 판의 핵심이야.'
      : '끝까지 따라왔으니 이제 판의 결은 봤어. 남은 건 운을 기다리는 게 아니라, 지금 읽힌 패턴을 실제 선택에서 다르게 쓰는 일이야.';
  const insightSections: InsightSection[] = [
    {
      title: '첫 장이 가리킨 건, 문제가 시작된 자리야',
      preview: '문제는 오늘 생긴 것처럼 보여도, 시작점은 더 앞에 있어.',
      body: spread === 'one-card'
        ? `한 장뿐인데도 판의 결이 분명해. 이 카드는 ${lastKeyword} 쪽을 바로 짚고 있어서, 지금 가장 먼저 들어야 할 말이 무엇인지 숨기지 않아.`
        : `${firstKeyword} 쪽에서 이미 출발점이 드러났어. 그래서 이번 판은 좋은 카드가 있느냐보다, 네가 언제부터 같은 방식으로 흔들렸는지를 먼저 읽는 편이 맞아.`,
    },
    {
      title: '표면 아래에 숨어 있던 진짜 압박은 이쪽이야',
      preview: '보이는 문제보다 안쪽의 버릇이 더 크게 작동하고 있어.',
      body: spread === 'one-card'
        ? `이 카드는 답을 돌려 말하지 않아. 지금 문제를 크게 만드는 건 상황보다 네 안의 망설임이라는 뜻이야.`
        : `${middleKeyword}${pickParticle(middleKeyword, '이가')} 이번 판의 가운데를 잡고 있어. 겉으로 보이는 문제보다, 흔들리면 안 된다는 압박이나 조급함 같은 안쪽 습관이 더 큰 변수가 되고 있다는 뜻이야.`,
    },
    {
      title: '이대로 두면 판은 이런 쪽으로 굳어질 가능성이 커',
      preview: '마지막 카드는 정답보다 방향을 먼저 보여줘.',
      body: `${lastKeyword} 쪽으로 판이 기울고 있어. 다만 저절로 좋아진다기보다, 지금부터 무엇을 붙들고 무엇을 놓을지 기준을 세울수록 카드의 좋은 면이 살아나.`,
    },
    {
      title: '묘안이 마지막으로 남기고 싶은 한마디',
      preview: '결국 이 리딩이 남기려는 건 기준이야.',
      body: `${nextAction} ${caution} ${closing}`,
    },
  ];

    return {
      opening: spread === 'one-card'
        ? '이번 질문은 한 장으로 답이 나와. 바로 핵심부터 짚어볼게.'
        : spread === 'three-card'
          ? '세 장을 따로 설명하진 않을게. 어디서 어긋났고, 지금 뭐가 제일 걸리며, 앞으로 어디로 기울지 순서대로 볼게.'
          : '이번 판은 겉만 보는 리딩이 아니야. 보이는 상황, 안쪽 감정, 주변 변수, 마지막 방향까지 층층이 따라가 보자.',
      summary: spread === 'one-card'
        ? `이번 질문의 핵심은 ${lastKeyword} 쪽이야. 지금 느끼는 불편함을 정확히 인정하는 게 먼저야.`
        : spread === 'three-card'
          ? `${firstKeyword} 쪽에서 시작된 흐름이 지금은 ${middleKeyword} 문제를 지나 ${lastKeyword} 쪽으로 기울고 있어. 지금은 감정보다 기준을 바로 세우는 쪽이 먼저야.`
          : `열 장을 따라가 보니, 판의 핵심이 ${firstKeyword}에서 ${lastKeyword} 쪽으로 옮겨가고 있어. 좋은 카드 하나에 기대기보다 반복되는 패턴을 어떻게 끊을지가 더 중요해.`,
    synthesis,
    detail,
    advice: nextAction,
    nextAction,
    caution,
    closing,
    cardReadings,
    insightSections,
  };
}

function inferDemoSpread(readingId: string): SpreadType | null {
  if (readingId.includes('celtic')) return 'celtic-cross';
  if (readingId.includes('one-card') || readingId.includes('onecard')) return 'one-card';
  if (readingId.includes('three-card') || readingId.includes('threecard')) return 'three-card';
  return null;
}

// Helper function to generate reading for demo mode
async function generateDemoReading(
  readingId: string,
  demoLlmMode: 'mock' | 'live'
): Promise<Reading> {
  const response = await fetch('/cards/meta.json');
  const cardMeta = await response.json() as { cards: TarotCard[] };
  const allCards = cardMeta.cards;

  const spread = inferDemoSpread(readingId) || (sessionStorage.getItem('selected_spread') as SpreadType) || 'three-card';
  const positionMeanings = getPositionMeanings(spread);
  const cardCount = positionMeanings.length;
  const cachedSelections = readDemoCardSelections(readingId, spread);
  const demoSelections = cachedSelections
    ?? generateDemoCardSelections(`${readingId}:${spread}`, spread);

  if (!cachedSelections) {
    writeDemoCardSelections(readingId, demoSelections);
  }

  const drawnCards: DrawnCard[] = demoSelections.slice(0, cardCount).map((selection, index) => {
    const cardData = allCards[selection.cardIndex] || allCards[0];
    return {
      card: {
        id: cardData.id,
        name_en: cardData.name_en,
        name_kr: cardData.name_kr,
        arcana: cardData.arcana,
        number: cardData.number,
        suit: cardData.suit,
        keywords_upright: cardData.keywords_upright,
        keywords_reversed: cardData.keywords_reversed,
        meaning_short: cardData.meaning_short,
      },
      orientation: selection.orientation,
      position: index,
      positionMeaning: positionMeanings[index],
    };
  });

  const topic = (sessionStorage.getItem('demo_topic') || '종합') as Topic;
  const question = sessionStorage.getItem('demo_question') || '';

  let result = buildDemoFallbackResult(spread, drawnCards);
  try {
    const llmResponse = await generateResult({
      topic,
      question: question || undefined,
      spread,
      llmMode: demoLlmMode,
      cards: drawnCards,
    });
    result = llmResponse.result;
  } catch (error) {
    console.error('Failed to generate LLM result. Using local fallback:', error);
  }

  return {
    id: readingId,
    topic,
    spread,
    question,
    cards: drawnCards,
    payment_status: 'paid',
    generation_status: 'completed',
    result,
    created_at: new Date().toISOString(),
  };
}

export default function ResultPage() {
  const { id, token } = useParams<{ id: string; token: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const headerRef = useRef<HTMLElement | null>(null);
  const bannerRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const scrollAnimationFrameRef = useRef<number | null>(null);
  const scrollSnapRestoreTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const trackedReadingViewRef = useRef<string | null>(null);

  const [reading, setReading] = useState<Reading | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pollCount, setPollCount] = useState(0);
  const [activeSection, setActiveSection] = useState(0);
  const [selectedCard, setSelectedCard] = useState<number | null>(null);
  const [openInsightIndex, setOpenInsightIndex] = useState(0);
  const [modalCardAnimation, setModalCardAnimation] = useState<ModalCardAnimation>('pop');
  const [headerHeight, setHeaderHeight] = useState(0);
  const [bannerHeight, setBannerHeight] = useState(0);
  const [summaryTopSafeOffset, setSummaryTopSafeOffset] = useState(132);

  const isDemoMode = id?.startsWith('demo-');
  const isSharedMode = location.pathname.startsWith('/share/');
  const demoLlmMode = searchParams.get('llm') === 'live' ? 'live' : 'mock';
  const isSummaryView = location.pathname.endsWith('/summary');
  const queryString = searchParams.toString();
  const readingKey = isSharedMode ? token : id;
  const resultPath = isSharedMode && token
    ? `/share/${token}${queryString ? `?${queryString}` : ''}`
    : id ? `/result/${id}${queryString ? `?${queryString}` : ''}` : '/';
  const summaryPath = isSharedMode && token
    ? `/share/${token}${queryString ? `?${queryString}` : ''}`
    : id ? `/result/${id}/summary${queryString ? `?${queryString}` : ''}` : '/';
  const totalSections = isSummaryView
    ? 1
    : (reading ? (reading.cards?.length || 0) + 1 : 2);

  useReadingFlowBackGuard(Boolean(id && !isDemoMode && !isSharedMode));

  useEffect(() => {
    setOpenInsightIndex(0);
  }, [reading?.id]);

  useEffect(() => {
    if (!reading?.id) return;

    const viewKey = `${reading.id}:${isSummaryView ? 'summary' : 'result'}:${isSharedMode ? 'shared' : 'owner'}`;
    if (trackedReadingViewRef.current === viewKey) return;
    trackedReadingViewRef.current = viewKey;

    trackEvent(isSummaryView ? 'summary_viewed' : 'reading_result_viewed', {
      reading_id_prefix: reading.id.slice(0, 8),
      spread: reading.spread || inferSpreadFromCount(reading.cards?.length || 0),
      card_count: reading.cards?.length || 0,
      shared: isSharedMode,
    });
  }, [isSharedMode, isSummaryView, reading?.cards?.length, reading?.id, reading?.spread]);

  const pickRandomModalAnimation = (): ModalCardAnimation => {
    const randomIndex = Math.floor(Math.random() * MODAL_CARD_ANIMATIONS.length);
    return MODAL_CARD_ANIMATIONS[randomIndex];
  };

  const openCardModal = (cardIndex: number) => {
    setModalCardAnimation(pickRandomModalAnimation());
    setSelectedCard(cardIndex);
  };

  const closeCardModal = () => {
    setSelectedCard(null);
  };

  const fetchReading = useCallback(async () => {
    if (!readingKey) return;

    if (isDemoMode) {
      try {
        const generatedReading = await generateDemoReading(readingKey, demoLlmMode);
        setReading(generatedReading);
        setError(null);
      } catch (err: any) {
        console.error('Failed to generate demo reading:', err);
        setError('데모 데이터를 생성할 수 없습니다.');
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      const response = isSharedMode
        ? await getSharedReading(readingKey)
        : await getReading(readingKey);
      setReading(response.reading);

      if (!isSharedMode && response.reading.payment_status !== 'paid') {
        navigate(`/payment/${readingKey}`, { replace: true });
        return;
      }

      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch reading:', err);
      setError(err.message || '리딩 정보를 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  }, [readingKey, isDemoMode, isSharedMode, navigate, demoLlmMode]);

  useEffect(() => {
    fetchReading();
  }, [fetchReading]);

  useEffect(() => {
    if (isDemoMode) return;
    if (!reading || reading.generation_status === 'completed' || reading.generation_status === 'failed') {
      return;
    }
    if (pollCount >= MAX_POLL_ATTEMPTS) {
      setError('리딩 생성 시간이 초과되었습니다.');
      return;
    }

    const timer = setTimeout(() => {
      fetchReading();
      setPollCount((prev) => prev + 1);
    }, POLL_INTERVAL);

    return () => clearTimeout(timer);
  }, [reading, pollCount, isDemoMode, fetchReading]);

  const clearScrollAnimation = () => {
    if (scrollAnimationFrameRef.current !== null) {
      cancelAnimationFrame(scrollAnimationFrameRef.current);
      scrollAnimationFrameRef.current = null;
    }
    if (scrollSnapRestoreTimeoutRef.current) {
      clearTimeout(scrollSnapRestoreTimeoutRef.current);
      scrollSnapRestoreTimeoutRef.current = null;
    }
  };

  const scrollToSection = (sectionIndex: number) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const clampedIndex = Math.min(totalSections - 1, Math.max(0, sectionIndex));
    const targetTop = clampedIndex * container.clientHeight;
    const startTop = container.scrollTop;
    const distance = targetTop - startTop;

    if (Math.abs(distance) < 1) return;

    clearScrollAnimation();

    const originalSnap = container.style.scrollSnapType;
    container.style.scrollSnapType = 'none';

    const duration = Math.min(1400, Math.max(600, Math.abs(distance) * 0.9));
    const startTime = performance.now();
    const easeInOutCubic = (value: number) => (
      value < 0.5
        ? 4 * value * value * value
        : 1 - Math.pow(-2 * value + 2, 3) / 2
    );

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(1, elapsed / duration);
      const easedProgress = easeInOutCubic(progress);
      container.scrollTop = startTop + distance * easedProgress;

      if (progress < 1) {
        scrollAnimationFrameRef.current = requestAnimationFrame(animate);
        return;
      }

      container.scrollTop = targetTop;
      scrollAnimationFrameRef.current = null;
      scrollSnapRestoreTimeoutRef.current = setTimeout(() => {
        container.style.scrollSnapType = originalSnap || '';
        scrollSnapRestoreTimeoutRef.current = null;
      }, 80);
    };

    scrollAnimationFrameRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;
    if (isSummaryView) {
      setActiveSection(0);
      return;
    }

    const updateActiveSection = () => {
      const sectionHeight = scrollContainer.clientHeight || 1;
      const currentSection = Math.round(scrollContainer.scrollTop / sectionHeight);
      const clampedSection = Math.min(totalSections - 1, Math.max(0, currentSection));
      setActiveSection(clampedSection);
    };

    updateActiveSection();
    scrollContainer.addEventListener('scroll', updateActiveSection, { passive: true });
    window.addEventListener('resize', updateActiveSection);

    return () => {
      scrollContainer.removeEventListener('scroll', updateActiveSection);
      window.removeEventListener('resize', updateActiveSection);
    };
  }, [isSummaryView, totalSections]);

  useEffect(() => {
    return () => {
      clearScrollAnimation();
    };
  }, []);

  const showApiSuccessBanner = false;
  const summaryExtraGap = typeof window !== 'undefined' && window.innerWidth >= 1024 ? 56 : 40;
  const headerClassName = isSummaryView
    ? 'fixed top-0 left-0 z-40 flex w-full items-center justify-between bg-[#131110]/72 px-3 py-1.5 sm:px-4 sm:py-2 md:px-5 md:py-2.5 backdrop-blur-[12px] shadow-none'
    : 'fixed top-0 left-0 z-40 flex w-full items-center justify-between bg-gradient-to-b from-[#131110]/95 via-[#131110]/72 to-transparent p-3 sm:p-4 md:p-6 lg:p-8 backdrop-blur-sm';
  const resolvedSummaryTopSafeOffset = Math.max(
    summaryTopSafeOffset,
    Math.ceil(
      headerHeight
      + (showApiSuccessBanner ? bannerHeight + 24 : 0)
      + (isSummaryView ? summaryExtraGap : 0)
    ),
  );

  useEffect(() => {
    const measureChrome = () => {
      const nextHeaderHeight = Math.ceil(headerRef.current?.getBoundingClientRect().height || 0);
      const nextBannerHeight = showApiSuccessBanner
        ? Math.ceil(bannerRef.current?.getBoundingClientRect().height || 0)
        : 0;

      setHeaderHeight(nextHeaderHeight);
      setBannerHeight(nextBannerHeight);

      const extraGap = isSummaryView ? summaryExtraGap + 10 : 10;
      const bannerGap = showApiSuccessBanner ? 24 : 0;
      setSummaryTopSafeOffset(nextHeaderHeight + nextBannerHeight + bannerGap + extraGap);
    };

    measureChrome();

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => measureChrome())
      : null;

    if (headerRef.current && resizeObserver) {
      resizeObserver.observe(headerRef.current);
    }

    if (bannerRef.current && resizeObserver) {
      resizeObserver.observe(bannerRef.current);
    }

    window.addEventListener('resize', measureChrome);
    window.addEventListener('orientationchange', measureChrome);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', measureChrome);
      window.removeEventListener('orientationchange', measureChrome);
    };
  }, [isSummaryView, showApiSuccessBanner]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#131110] flex items-center justify-center">
        <div className="text-[#fbbf24] text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#fbbf24] mx-auto mb-4"></div>
          <p className="text-sm tracking-wider">리딩을 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error || !reading) {
    return (
      <div className="min-h-screen bg-[#131110] flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <p className="text-red-400 mb-4">{error || '리딩을 찾을 수 없습니다.'}</p>
          <button
            onClick={() => navigate('/')}
            className="text-[#fbbf24] hover:text-white transition-colors"
          >
            홈으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  const isLeftAligned = (index: number) => index % 2 === 0;
  const resolvedSpread = reading.spread || inferSpreadFromCount(reading.cards?.length || 0);
  const fallbackResult = reading.cards?.length
    ? buildDemoFallbackResult(resolvedSpread, reading.cards)
    : undefined;
  const fallbackCardReadings = fallbackResult?.cardReadings;
  const cardReadings = getStructuredCardReadings(reading.result, fallbackCardReadings);
  const insightSections = getStructuredInsightSections(reading.result, fallbackResult, reading.cards);
  const finalOpening = compactReadingText(reading.result?.opening)
    || '한 장씩 따라오다 보니 이제 판의 결이 꽤 또렷해졌네. 여기서 전체 흐름을 한 번 묶어볼게.';
  const finalSynthesis = compactReadingText(reading.result?.synthesis)
    || compactReadingText(reading.result?.summary)
    || '이번 리딩의 전체 흐름을 정리하는 중입니다.';
  const finalNextAction = compactReadingText(reading.result?.nextAction)
    || compactReadingText(reading.result?.advice)
    || '지금 붙들고 있는 문제에서 가장 작은 행동 하나부터 정해 보자.';
  const finalCaution = compactReadingText(reading.result?.caution)
    || '조급함이 먼저 움직이지 않도록, 감정과 사실을 나눠서 보자.';
  const finalClosing = compactReadingText(reading.result?.closing)
    || '여기서 끝이 아니라, 이제부터 네가 어떻게 움직이는지가 진짜 결과를 만든다.';
  const modalCardData = selectedCard !== null ? reading.cards?.[selectedCard] : undefined;
  const modalCardKeywords = modalCardData
    ? (modalCardData.orientation === 'upright'
      ? modalCardData.card.keywords_upright
      : modalCardData.card.keywords_reversed)
    : [];

  return (
    <div className="bg-[#131110] font-serif text-white overflow-x-hidden min-h-screen selection:bg-[#bd3b0f] selection:text-white">
      {/* Grain Overlay */}
      <div className="fixed inset-0 pointer-events-none z-50 mix-blend-overlay opacity-20 bg-[url('data:image/svg+xml,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noiseFilter%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.65%22 numOctaves=%223%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noiseFilter)%22 opacity=%220.05%22/%3E%3C/svg%3E')]" />

      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        .scroll-snap-container { scroll-snap-type: y mandatory; }
        .scroll-snap-item { scroll-snap-align: start; scroll-snap-stop: always; }
        @media (max-width: 1023px) {
          .scroll-snap-container { scroll-snap-type: none; }
          .scroll-snap-item { scroll-snap-align: none; scroll-snap-stop: normal; }
        }
        .card-stack-shadow { box-shadow: 0 30px 60px -12px rgba(0, 0, 0, 0.9); }
        .card-tilt-container { perspective: 1000px; }
        .card-tilt {
          transition: transform 0.6s cubic-bezier(0.23, 1, 0.32, 1);
          transform-style: preserve-3d;
          transform: rotateY(var(--card-tilt-y, -5deg)) rotateX(2deg);
        }
        .card-tilt-container:hover .card-tilt {
          transform: rotateY(0) rotateX(0) scale(1.02);
          z-index: 20;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes cardEntrance {
          0% {
            opacity: 0;
            transform: translateY(18px) scale(0.94) rotate(-2deg);
          }
          65% {
            opacity: 1;
            transform: translateY(-4px) scale(1.02) rotate(0.8deg);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1) rotate(0deg);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-scaleIn {
          animation: scaleIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        .animate-cardEntrance {
          animation: cardEntrance 0.65s cubic-bezier(0.22, 1, 0.36, 1);
          will-change: transform, opacity;
        }
      `}</style>

      {/* Header */}
      <header
        ref={headerRef}
        className={headerClassName}
      >
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={() => navigate(isSummaryView ? resultPath : '/')}
            className="flex items-center justify-center w-9 h-9 rounded-full border border-[#c9a092]/30 text-[#c9a092]/80 hover:text-white hover:border-[#fbbf24]/50 transition-colors active:scale-95 sm:w-9.5 sm:h-9.5"
            aria-label={isSummaryView ? '카드 리딩으로 돌아가기' : '홈으로'}
          >
            <UiIcon name="arrow-left" className="h-4.5 w-4.5 sm:h-4.5 sm:w-4.5" />
          </button>
          <Link
            to="/"
            aria-label="메인 페이지로 이동"
            className="flex items-center gap-2 transition-colors hover:text-white focus:outline-none focus:ring-2 focus:ring-[#fbbf24]/60 focus:ring-offset-2 focus:ring-offset-[#131110] sm:gap-3"
          >
            <UiIcon name="sparkles" className="h-4 w-4 text-[#c9a092] sm:h-4 sm:w-4" />
            <span className="text-[9px] uppercase tracking-[0.18em] sm:text-[9px] sm:tracking-[0.2em] md:text-[11px] md:tracking-[0.26em] text-[#c9a092] font-bold">
              {isSummaryView ? '묘안 최종 정리' : '묘안 리딩'}
            </span>
          </Link>
        </div>
      </header>

      {/* Progress Indicator */}
      {!isSummaryView && (
        <div className="fixed left-8 top-1/2 -translate-y-1/2 z-40 hidden lg:flex flex-col items-center gap-6">
        <span className="text-[10px] uppercase tracking-widest text-[#fbbf24]/40 rotate-180 [writing-mode:vertical-lr]">리딩 흐름</span>
        <div className="flex flex-col gap-3">
          {Array.from({ length: totalSections }, (_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => scrollToSection(index)}
              className="w-4 h-8 flex items-center justify-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#fbbf24]/60 rounded-sm"
            >
              <span
                className={`w-1 h-8 border transition-colors ${activeSection === index
                  ? 'bg-[#fbbf24] border-[#fbbf24]/50'
                  : activeSection > index
                    ? 'bg-[#fbbf24]/30 border-[#fbbf24]/20'
                    : 'bg-white/5 border-white/10'
                  }`}
              />
            </button>
          ))}
        </div>
        <span className="text-xs font-serif text-[#fbbf24]/60">{activeSection + 1} / {totalSections}</span>
        </div>
      )}

      {/* Main Content */}
      <main
        ref={scrollContainerRef}
        className={isSummaryView
          ? 'min-h-[100dvh] overflow-y-auto hide-scrollbar relative'
          : 'h-[100dvh] overflow-y-auto lg:overflow-y-scroll scroll-snap-container hide-scrollbar relative'}
      >
        {/* Card Sections */}
        {!isSummaryView && reading.cards?.map((drawnCard, index) => {
          const leftAligned = isLeftAligned(index);
          const cardImageSrc = getCardFrontImageSrc(drawnCard.card);
          const cardReading = cardReadings[index];
          const displayPosition = getDisplayPositionMeaning(resolvedSpread, drawnCard, index);
          const celticChapter = getCelticChapter(index, resolvedSpread);
          const cardHook = compactReadingText(cardReading?.hook)
            || `${displayPosition}에서는 이 카드가 먼저 눈에 들어와.`;
          const cardBody = compactReadingText(cardReading?.reading)
            || compactReadingText(cardReading?.narration)
            || `${drawnCard.card.name_kr} 카드가 지금 상황에서 놓치기 쉬운 부분을 짚고 있어.`;
          const cardBodyParagraphs = splitDisplayParagraphs(cardBody);
          const cardConnection = compactReadingText(cardReading?.connection);
          const cardBridge = compactReadingText(cardReading?.bridge);
          return (
            <section
              key={index}
              className="scroll-snap-item min-h-[100dvh] lg:h-screen w-full relative flex items-center justify-center overflow-visible lg:overflow-hidden bg-gradient-to-b from-[#131110] via-[#2d1a1a]/20 to-[#131110] py-28 sm:py-24 md:py-20 lg:py-0"
            >
              <div className="absolute inset-0 opacity-30 pointer-events-none bg-[url('data:image/svg+xml,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Ccircle cx=%2210%22 cy=%2210%22 r=%220.5%22 fill=%22white%22 opacity=%220.2%22/%3E%3C/svg%3E')]" />

              <div className="max-w-6xl w-full grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-20 px-4 sm:px-5 md:px-6">
                {/* Card */}
                <div className={`flex justify-center items-center card-tilt-container ${leftAligned ? 'lg:justify-end' : 'lg:justify-start lg:order-2'}`}>
                  <div
                    onClick={() => openCardModal(index)}
                    style={{ ['--card-tilt-y' as string]: leftAligned ? '5deg' : '-5deg' }}
                    className="relative w-[200px] xs:w-[220px] sm:w-[260px] md:w-[300px] lg:w-[380px] xl:w-[400px] aspect-[4/7] bg-[#110e0d] border border-[#fbbf24]/30 rounded-sm p-2.5 sm:p-3 md:p-4 card-stack-shadow flex flex-col items-center card-tilt touch-manipulation cursor-pointer hover:border-[#fbbf24]/50 transition-colors">
                    <div className="absolute inset-1.5 sm:inset-2 border border-[#fbbf24]/10 pointer-events-none" />
                    <div className="mb-3 sm:mb-4 text-center">
                      <span className="text-[#fbbf24]/80 text-[9px] sm:text-[10px] tracking-[0.4em] sm:tracking-[0.5em] uppercase font-serif block mb-1">
                        {index + 1}번째 카드
                      </span>
                      <h2 className="text-[#fbbf24] text-lg sm:text-xl md:text-2xl tracking-[0.08em] sm:tracking-[0.1em] font-light leading-tight px-2">{drawnCard.card.name_kr}</h2>
                      <p className="text-[#fbbf24]/60 text-[8px] sm:text-[9px] tracking-wide sm:tracking-wider uppercase mt-1">{drawnCard.card.name_en}</p>
                    </div>
                    <div className="relative flex-grow w-full border border-[#fbbf24]/20 overflow-hidden bg-black">
                      <img
                        alt={drawnCard.card.name_kr}
                        className="w-full h-full object-cover transition-transform duration-500"
                        src={cardImageSrc}
                        style={{
                          transform: drawnCard.orientation === 'reversed' ? 'rotate(180deg)' : 'rotate(0deg)'
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-80" />
                    </div>
                    <div className="mt-4 flex items-center gap-4">
                      <div className="h-[1px] w-8 bg-[#fbbf24]/20" />
                      <span className="text-[#fbbf24]/60 text-[9px] tracking-widest uppercase italic">
                        {drawnCard.orientation === 'upright' ? '정방향' : '역방향'}
                      </span>
                      <div className="h-[1px] w-8 bg-[#fbbf24]/20" />
                    </div>
                  </div>
                </div>

                {/* Interpretation Text */}
                <div className={`flex flex-col justify-center ${!leftAligned ? 'lg:order-1 lg:items-end' : ''}`}>
                  <div className="w-full max-w-md space-y-6">
                    {celticChapter && celticChapter.start === index && (
                      <div className="border border-[#fbbf24]/18 bg-[#17120f]/78 px-4 py-4 shadow-[0_12px_32px_-22px_rgba(0,0,0,0.85)]">
                        <p className="text-[10px] uppercase tracking-[0.22em] text-[#fbbf24]/58">켈틱 구간</p>
                        <p className="mt-2 text-lg tracking-[0.04em] text-[#f8e8d1]">{celticChapter.title}</p>
                        <p className="mt-2 text-xs leading-6 text-[#e0ccb2]/72">{celticChapter.subtitle}</p>
                      </div>
                    )}

                    <div className="relative overflow-visible py-2">
                      <div className="pointer-events-none absolute inset-0 z-0 flex items-center justify-between px-0 sm:px-1">
                        <span
                          className="text-[8.8rem] font-semibold leading-none sm:text-[13rem]"
                          style={{ color: '#241C19', opacity: 0.9 }}
                        >
                          “
                        </span>
                        <span
                          className="text-[8.8rem] font-semibold leading-none sm:text-[13rem]"
                          style={{ color: '#241C19', opacity: 0.9 }}
                        >
                          ”
                        </span>
                      </div>
                      <div className="relative z-10 flex min-h-[7.8rem] items-center justify-center px-2 sm:min-h-[10rem] sm:px-4">
                        <h3 className="max-w-[28rem] text-center text-[1.7rem] font-semibold leading-[1.48] tracking-[0.01em] text-[#f3c867] [text-shadow:0_0_18px_rgba(251,191,36,0.24)] sm:max-w-[36rem] sm:text-[2.15rem] sm:leading-[1.35]">
                          {cardHook}
                        </h3>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-start gap-4 sm:gap-5">
                      <div className="min-w-[6rem]">
                        <p className="text-lg font-semibold tracking-[0.05em] text-[#f6e4c8] sm:text-[1.35rem]">
                          {displayPosition}
                        </p>
                        <p className="mt-2 text-[11px] tracking-[0.06em] text-[#fbbf24]/72 sm:text-xs">
                          {reading.cards?.length || 0}장 중 {index + 1}번째 카드
                        </p>
                      </div>

                      <div className="ml-auto flex flex-col items-center gap-2">
                        <div className="relative h-16 w-24 sm:h-20 sm:w-32 bg-[#0a0a0a]/50 border border-[#fbbf24]/20 rounded flex-shrink-0">
                          <div className="absolute inset-0 p-1">
                            {[
                              ...Array.from({ length: reading.cards?.length || 0 }, (_, idx) => idx)
                            ].map((idx) => {
                              const total = reading.cards?.length || 1;
                              const x = total === 1 ? 50 : total === 3 ? (idx === 0 ? 20 : idx === 1 ? 50 : 80) : idx < 6 ? [35, 43, 38.5, 38.5, 10, 67][idx] : 88;
                              const y = total === 1 ? 50 : total === 3 ? 50 : idx < 6 ? [50, 50, 20, 80, 50, 50][idx] : [80, 62, 44, 26][idx - 6];
                              const rotation = total === 10 && idx === 1 ? -90 : 0;
                              const scale = total === 1 ? 1.2 : total === 3 && idx === 1 ? 1.1 : total === 10 && idx === 1 ? 0.9 : 1;

                              return (
                                <div
                                  key={idx}
                                  className={`absolute transition-all duration-300 ${idx === index
                                    ? 'bg-[#fbbf24]'
                                    : 'bg-[#fbbf24]/20'
                                    }`}
                                  style={{
                                    left: `${x}%`,
                                    top: `${y}%`,
                                    width: '6px',
                                    height: '10px',
                                    transform: `translate(-50%, -50%) rotate(${rotation}deg) scale(${scale})`,
                                    borderRadius: '1px',
                                  }}
                                />
                              );
                            })}
                          </div>
                        </div>
                        <span className="text-[9px] text-[#fbbf24]/45 uppercase tracking-[0.18em]">스프레드 맵</span>
                      </div>
                    </div>

                    {/* Cat Fortune Teller Speech Bubble */}
                    <div className="relative">
                      <div className="relative bg-[#1a1512]/80 border border-[#fbbf24]/20 rounded-lg rounded-tl-none px-4 pb-4 pt-0 shadow-lg space-y-4">
                        <div className="absolute -top-[1px] -left-[1px] w-3 h-3 border-t border-l border-[#fbbf24]/20 bg-[#1a1512]/80" style={{ clipPath: 'polygon(0 0, 100% 0, 0 100%)' }} />
                        <div className="space-y-2 break-words font-serif">
                          {cardBodyParagraphs.map((paragraph, paragraphIndex) => (
                            <p
                              key={`${drawnCard.card.id}-body-${paragraphIndex}`}
                              className="text-[#f5f5f4]/85 text-xs sm:text-sm leading-relaxed"
                            >
                              {paragraph}
                            </p>
                          ))}
                        </div>
                        {cardConnection && (
                          <div className="border-l border-[#fbbf24]/25 pl-3">
                            <p className="text-[#e6d3bc]/75 text-[11px] sm:text-xs leading-relaxed break-words italic">
                              {cardConnection}
                            </p>
                          </div>
                        )}
                        {cardBridge && (
                          <p className="text-[#fbbf24]/65 text-[11px] sm:text-xs leading-relaxed break-words uppercase tracking-[0.12em]">
                            {cardBridge}
                          </p>
                        )}
                      </div>
                      <p className="text-[10px] text-[#fbbf24]/40 mt-2 italic">
                        카드를 누르면 기본 정보와 상징을 더 볼 수 있어요.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {(index < (reading.cards?.length || 0) - 1 || !isSummaryView) && (
                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 text-[#fbbf24]/30 cursor-default">
                  <UiIcon name="arrow-down-double" className="h-6 w-6" />
                </div>
              )}
            </section>
          );
        })}

        {/* Card Detail Modal */}
        {selectedCard !== null && reading.cards && reading.cards[selectedCard] && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn"
            onClick={closeCardModal}
          >
            <div
              className="relative w-full max-w-5xl bg-[#131110] border border-[#fbbf24]/30 rounded-lg overflow-hidden shadow-2xl animate-scaleIn"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={closeCardModal}
                className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-[#1a1512]/80 border border-[#fbbf24]/30 text-[#fbbf24] hover:bg-[#fbbf24]/10 hover:border-[#fbbf24]/50 transition-colors"
              >
                <UiIcon name="close" className="h-5 w-5" />
              </button>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-6 sm:p-8 md:p-10">
                {/* Left: Card with Entrance Animation */}
                <div className="flex items-center justify-center" style={{ perspective: '1200px' }}>
                  <div
                    key={`${selectedCard}-${modalCardAnimation}`}
                    className="relative w-[240px] sm:w-[280px] md:w-[320px] aspect-[4/7] animate-cardEntrance"
                  >
                    <div
                      className="relative w-full h-full bg-[#110e0d] border border-[#fbbf24]/30 rounded-sm p-3 md:p-4 shadow-2xl"
                    >
                      <div className="absolute inset-2 border border-[#fbbf24]/10 pointer-events-none" />
                      <div className="mb-3 text-center">
                        <span className="text-[#fbbf24]/80 text-[10px] tracking-[0.5em] uppercase font-serif block mb-1">
                          {selectedCard + 1}번째 카드
                        </span>
                        <h2 className="text-[#fbbf24] text-xl md:text-2xl tracking-[0.1em] font-light leading-tight">
                          {reading.cards[selectedCard].card.name_kr}
                        </h2>
                        <p className="text-[#fbbf24]/60 text-[9px] tracking-wider uppercase mt-1">
                          {reading.cards[selectedCard].card.name_en}
                        </p>
                      </div>
                      <div className="relative flex-grow w-full border border-[#fbbf24]/20 overflow-hidden bg-black" style={{ height: 'calc(100% - 100px)' }}>
                        <img
                          alt={reading.cards[selectedCard].card.name_kr}
                          className="w-full h-full object-cover"
                          src={getCardFrontImageSrc(reading.cards[selectedCard].card)}
                          style={{
                            transform: reading.cards[selectedCard].orientation === 'reversed' ? 'rotate(180deg)' : 'rotate(0deg)'
                          }}
                        />
                      </div>
                      <div className="mt-3 flex items-center justify-center gap-3">
                        <div className="h-[1px] w-6 bg-[#fbbf24]/20" />
                        <span className="text-[#fbbf24]/60 text-[9px] tracking-widest uppercase italic">
                          {reading.cards[selectedCard].orientation === 'upright' ? '정방향' : '역방향'}
                        </span>
                        <div className="h-[1px] w-6 bg-[#fbbf24]/20" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right: Detailed Information */}
                <div className="flex flex-col justify-center space-y-6 max-h-[70vh] overflow-y-auto pr-2">
                  <div>
                    <div className="flex items-center gap-3 text-[#fbbf24]/40 mb-4">
                      <span className="text-[10px] uppercase tracking-widest">위치</span>
                      <div className="h-px flex-grow bg-[#fbbf24]/20" />
                    </div>
                    <p className="text-[#fbbf24] text-sm">{getDisplayPositionMeaning(resolvedSpread, reading.cards[selectedCard], selectedCard)}</p>
                  </div>

                  <div>
                    <h4 className="text-[#fbbf24]/60 text-xs uppercase tracking-wider mb-3">키워드</h4>
                    <div className="flex flex-wrap gap-2">
                      {modalCardKeywords?.map((keyword, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1.5 bg-[#fbbf24]/10 border border-[#fbbf24]/20 rounded-full text-[#fbbf24]/80 text-xs"
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-[#fbbf24]/60 text-xs uppercase tracking-wider mb-3">카드 정보</h4>
                    <div className="space-y-2 text-sm text-[#f5f5f4]/70">
                      <p><span className="text-[#fbbf24]/60">방향:</span> {reading.cards[selectedCard].orientation === 'upright' ? '정방향' : '역방향'}</p>
                      <p><span className="text-[#fbbf24]/60">아르카나:</span> {reading.cards[selectedCard].card.arcana === 'major' ? '메이저 아르카나' : '마이너 아르카나'}</p>
                      {reading.cards[selectedCard].card.suit && (
                        <p><span className="text-[#fbbf24]/60">수트:</span> {getSuitLabel(reading.cards[selectedCard].card.suit)}</p>
                      )}
                      <p><span className="text-[#fbbf24]/60">번호:</span> {getCardDisplayNumber(reading.cards[selectedCard].card)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {!isSummaryView && (
          <section className="scroll-snap-item min-h-[100dvh] w-full relative flex items-center justify-center overflow-visible bg-gradient-to-b from-[#131110] via-[#1a1411]/92 to-[#131110] px-4 py-24 sm:px-6 lg:px-8">
            <div className="w-full max-w-3xl border border-[#fbbf24]/20 bg-[#15110f]/90 p-6 text-center shadow-[0_30px_70px_-30px_rgba(0,0,0,0.95)] backdrop-blur-md sm:p-8 md:p-10">
              <p className="text-[10px] uppercase tracking-[0.26em] text-[#fbbf24]/58">카드 리딩 완료</p>
              <h2 className="mt-4 text-2xl leading-tight tracking-[0.04em] text-[#f8e8d1] sm:text-3xl">
                마지막으로 이 판 전체를 한 번에 묶어볼게.
              </h2>
              <p className="mt-4 text-sm leading-7 text-[#e5d2b7]/78 sm:text-[15px]">
                지금까지 나온 흐름을 한 번에 묶어볼 차례야.
                <br className="hidden sm:block" />
                전체 흐름과 마지막 조언만 따로 모아서 읽을 수 있어.
              </p>
              <div className="mt-8 flex justify-center">
                <button
                  type="button"
                  onClick={() => navigate(summaryPath)}
                  className="inline-flex items-center gap-2 border border-[#fbbf24]/32 bg-[#1a1512]/95 px-5 py-4 text-[11px] uppercase tracking-[0.24em] text-[#fbbf24]/88 transition-colors hover:border-[#fbbf24]/58 hover:bg-[#241a15]"
                >
                  <UiIcon name="sparkles" className="h-4 w-4" />
                  최종 정리 보러 가기
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Final Summary Bridge Section */}
        {isSummaryView && (
          <section
            className="min-h-[100dvh] w-full relative flex items-start justify-center overflow-visible bg-gradient-to-b from-[#131110] via-[#1f1714]/80 to-[#131110] px-0 pb-20 sm:pb-20 md:pb-20 lg:pb-24"
            style={{ paddingTop: `${resolvedSummaryTopSafeOffset}px` }}
          >
          <div
            className="w-full max-w-5xl px-4 sm:px-5 md:px-6 relative z-10 flex flex-col items-center justify-start"
            style={{ scrollMarginTop: `${resolvedSummaryTopSafeOffset}px` }}
          >

            {/* Center Container */}
            <div className="w-full max-w-none relative">

              <div className="space-y-3 sm:space-y-3.5 lg:space-y-4 w-full flex flex-col items-center text-center">

                {/* Section Header */}
                <div className="w-full max-w-3xl space-y-1.5">
                  <div className="flex items-center justify-center w-full">
                    <span className="text-[14px] sm:text-[15px] uppercase tracking-[0.22em] sm:tracking-[0.28em] font-bold text-[#f4d58d]">
                      최종 정리
                    </span>
                  </div>
                  <p className="text-[11px] leading-5 text-[#ead9bf]/84 sm:text-[12px] sm:leading-6">
                    흩어져 있던 카드들을 한 문맥으로 다시 묶어보는 구간이야.
                  </p>
                </div>

                {/* Stylized Message Box */}
                <div className="relative group w-full max-w-[min(74rem,100%)]">
                  <div className={`relative border border-[#fbbf24]/28 bg-[#15120f]/95 rounded-sm shadow-[0_20px_50px_-15px_rgba(0,0,0,0.9)] backdrop-blur-md ${resolvedSpread === 'celtic-cross' ? 'p-3 sm:p-4 md:p-6 lg:p-7' : 'p-3.5 sm:p-4.5 md:p-6 lg:p-7'}`}>
                    <p className={`text-[#f5f5f4] font-serif italic tracking-wide break-keep break-words text-center whitespace-pre-line ${resolvedSpread === 'celtic-cross' ? 'text-[12.5px] leading-6 sm:text-[15px] sm:leading-7 md:text-[1.06rem] md:leading-8' : 'text-[13px] leading-6 sm:text-[16px] sm:leading-7 md:text-[1.08rem] md:leading-8'}`}>
                      {finalOpening}
                    </p>

                    <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-[#fbbf24]/30" />
                    <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-[#fbbf24]/30" />
                    <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-[#fbbf24]/30" />
                    <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-[#fbbf24]/30" />
                  </div>
                </div>

                {insightSections.length > 0 && (
                  <div className="w-full space-y-2.5 sm:space-y-3 text-left">
                    <div className="flex justify-center sm:justify-start">
                      <span className="inline-flex items-center border border-[#fbbf24]/18 bg-[#18120f]/82 px-3 py-1 text-[12px] sm:text-[13px] uppercase tracking-[0.18em] sm:tracking-[0.22em] text-[#f4d58d] font-semibold shadow-[0_10px_24px_-18px_rgba(0,0,0,0.85)]">
                        묘안 해설
                      </span>
                    </div>

                    {insightSections.map((section, index) => {
                      const isOpen = openInsightIndex === index;
                      return (
                        <article
                          key={`${section.title}-${index}`}
                          className="overflow-hidden border border-[#7A5317]/85 bg-[#120e0b]/92 shadow-[0_18px_40px_-24px_rgba(0,0,0,0.95)]"
                        >
                          <button
                            type="button"
                            className="relative flex w-full items-start justify-between gap-3 bg-[linear-gradient(135deg,rgba(51,28,22,0.98),rgba(30,19,16,0.98))] px-4 py-3.5 pr-12 text-left sm:px-5 sm:py-4 sm:pr-14"
                            onClick={() => setOpenInsightIndex((current) => (current === index ? -1 : index))}
                          >
                            <div className="min-w-0 max-w-[calc(100%-2.75rem)] sm:max-w-[calc(100%-3.5rem)]">
                              <p className="text-[11px] uppercase tracking-[0.14em] sm:tracking-[0.18em] text-[#f4d58d] font-semibold">
                                {index + 1}번째 해설
                              </p>
                              <h3 className="mt-1.5 break-keep pr-1 text-[13px] font-semibold leading-[1.5] text-[#f5ede1] sm:text-[16px] sm:leading-[1.42] lg:text-[18px]">
                                {section.title}
                              </h3>
                            </div>
                            <span className="absolute right-3.5 top-1/2 flex h-8 w-8 shrink-0 -translate-y-1/2 items-center justify-center rounded-full border border-[#8d6a20]/85 bg-[#241713] text-[#e5bf65] sm:right-5">
                              <UiIcon name={isOpen ? 'chevron-up' : 'chevron-down'} className="h-4 w-4" />
                            </span>
                          </button>

                          {isOpen && (
                            <div className="border-t border-[#7A5317]/85 bg-[linear-gradient(180deg,rgba(22,15,12,0.96),rgba(17,13,11,0.98))] px-5 py-5">
                              {section.preview && (
                                <p className="mb-4 border-l border-[#9c6a2a]/70 pl-3 text-[13px] leading-6 text-[#ecd9b9]/88 italic break-keep">
                                  {section.preview}
                                </p>
                              )}
                              <p className="text-sm sm:text-[15px] leading-7 text-[#f5ede1] whitespace-pre-line break-keep">
                                {section.body}
                              </p>
                            </div>
                          )}
                        </article>
                      );
                    })}
                  </div>
                )}

                <div className="grid w-full gap-3.5 sm:gap-4 md:grid-cols-3">
                  <article className="border border-[#fbbf24]/20 bg-[#17120f]/80 p-5 text-left shadow-[0_18px_40px_-22px_rgba(0,0,0,0.9)]">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#f4d58d]/84">전체 흐름</p>
                    <p className="mt-3 text-sm sm:text-[15px] leading-relaxed text-[#f5ede1] whitespace-pre-line">
                      {finalSynthesis}
                    </p>
                  </article>
                  <article className="border border-[#fbbf24]/20 bg-[#17120f]/80 p-5 text-left shadow-[0_18px_40px_-22px_rgba(0,0,0,0.9)]">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#f4d58d]/84">지금 할 일</p>
                    <p className="mt-3 text-sm sm:text-[15px] leading-relaxed text-[#f5ede1] whitespace-pre-line">
                      {finalNextAction}
                    </p>
                  </article>
                  <article className="border border-[#fbbf24]/20 bg-[#17120f]/80 p-5 text-left shadow-[0_18px_40px_-22px_rgba(0,0,0,0.9)]">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#f4d58d]/84">놓치지 말 것</p>
                    <p className="mt-3 text-sm sm:text-[15px] leading-relaxed text-[#f5ede1] whitespace-pre-line">
                      {finalCaution}
                    </p>
                  </article>
                </div>

                <div className="w-full max-w-2xl border border-[#fbbf24]/15 bg-[#120f0d]/70 px-5 py-5 text-center">
                  <p className="text-[10px] uppercase tracking-[0.24em] text-[#f4d58d]/76">마지막 한마디</p>
                  <p className="mt-3 text-sm sm:text-base leading-relaxed text-[#f3dfc5] italic whitespace-pre-line">
                    {finalClosing}
                  </p>
                </div>

                {/* Call to Action Buttons */}
                <div className="pt-4 sm:pt-6 w-full max-w-xl mx-auto grid gap-3 sm:grid-cols-2">
                  <button
                    onClick={() => navigate(resultPath)}
                    className="w-full border border-[#fbbf24]/24 bg-[#17120f]/85 px-4 py-4 text-[11px] uppercase tracking-[0.24em] text-[#f3dfc5] transition-colors hover:border-[#fbbf24]/45 hover:bg-[#1e1712]"
                  >
                    카드 리딩으로 돌아가기
                  </button>
                  <button
                    onClick={() => navigate(`/spreads`)}
                    className="relative w-full overflow-hidden border border-[#fbbf24]/50 sm:border-2 sm:border-[#fbbf24]/60 bg-[#1a120f] px-4 py-4 sm:px-6 sm:py-5 rounded-sm transition-colors hover:border-[#fbbf24] hover:bg-[#23170f] active:scale-[0.98] cursor-pointer"
                  >
                    <span className="relative z-10 flex items-center justify-center text-[15px] sm:text-base font-bold tracking-[0.2em] text-[#fbbf24]">
                      새 리딩 시작하기
                    </span>
                  </button>
                </div>

              </div>
            </div>
          </div>
          </section>
        )}
      </main>
    </div>
  );
}
